// src/components/auth/LoginPage.tsx
import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Loader, Shield, Database } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const { signIn, loading, error } = useAuth();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLocalError('');

    if (!formData.email || !formData.password) {
      setLocalError('Veuillez remplir tous les champs');
      return;
    }

    const result = await signIn(formData.email, formData.password);
    
    if (!result.success) {
      setLocalError(result.error || 'Erreur de connexion');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Réinitialiser les erreurs quand l'utilisateur tape
    if (localError || error) {
      setLocalError('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header avec animation */}
        <div className="text-center mb-8">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 w-20 h-20 rounded-3xl mx-auto blur opacity-30 animate-pulse"></div>
            <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 w-20 h-20 rounded-3xl mx-auto flex items-center justify-center transform hover:scale-110 transition-transform duration-300">
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
            Focal Audit Pro
          </h1>
          <p className="text-gray-600 text-lg">Système d'audit nouvelle génération</p>
          <div className="flex items-center justify-center mt-3 gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-500">Connecté DB</span>
          </div>
        </div>

        {/* Formulaire de connexion ultra-moderne */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
          {/* Affichage des erreurs */}
          {(error || localError) && (
            <div className="mb-6 p-4 bg-red-50/80 backdrop-blur border border-red-200 rounded-2xl flex items-start space-x-3">
              <AlertCircle className="text-red-500 w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-semibold">Erreur de connexion</p>
                <p className="text-red-700 text-sm mt-1">{localError || error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                <Mail className="inline-block w-4 h-4 mr-2" />
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-4 rounded-2xl border-0 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400"
                placeholder="votre-email@focal.com"
                disabled={loading}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                <Lock className="inline-block w-4 h-4 mr-2" />
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-4 rounded-2xl border-0 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400 pr-12"
                  placeholder="••••••••••••"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.email || !formData.password}
              className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-4 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin w-6 h-6 mr-3" />
                  Connexion en cours...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5 mr-2" />
                  Se connecter
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;